import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Check, Clock, Hand, Moon, Repeat, Sun } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Logo } from "@/components/Logo";
import { api, money } from "@/lib/api";
import { DemoButton } from "@/components/DemoButton";
import { usePageMeta } from "@/lib/seo";

const ICONS = [CalendarDays, Moon, Repeat, Hand, Clock, Sun];

export const Header = () => (
  <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/80 backdrop-blur-xl">
    <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8 lg:px-12">
      <Logo />
      <nav className="hidden gap-8 text-sm font-medium text-slate-600 md:flex">
        <Link to="/tour" className="hover:text-slate-900" data-testid="nav-tour-link">Product tour</Link>
        <a href="/#features" className="hover:text-slate-900" data-testid="nav-features-link">Features</a>
        <a href="/#pricing" className="hover:text-slate-900" data-testid="nav-pricing-link">Pricing</a>
        <a href="/#faq" className="hover:text-slate-900" data-testid="nav-faq-link">FAQ</a>
      </nav>
      <div className="flex items-center gap-2">
        <DemoButton label="Live demo" className="hidden rounded-full px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 sm:flex" testId="header-demo-btn" />
        <Link to="/login" className="rounded-full px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100" data-testid="header-login-btn">Log in</Link>
        <Link to="/signup" className="rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700" data-testid="header-signup-btn">Start free</Link>
      </div>
    </div>
  </header>
);

const PREVIEW = [
  ["Amy", [["07:00", "15:00", "#4F46E5"], null, ["10:00", "10:30", "#9333EA", true], null, ["07:00", "15:00", "#4F46E5"]]],
  ["Ben", [null, ["14:00", "22:00", "#0D9488"], null, ["21:30", "07:30", "#334155", true], null]],
  ["Chloe", [["14:00", "22:00", "#0D9488"], ["07:00", "15:00", "#4F46E5"], null, null, ["10:00", "10:30", "#9333EA", true]]],
];

const RotaPreview = () => (
  <div className="rise d2 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl shadow-indigo-100" data-testid="hero-rota-preview">
    <div className="mb-3 flex items-center justify-between">
      <p className="font-display text-sm font-bold">This week</p>
      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Published</span>
    </div>
    <div className="grid grid-cols-[56px_repeat(5,1fr)] gap-1.5 text-[10px]">
      <span />
      {["Mon", "Tue", "Wed", "Thu", "Fri"].map((d) => <span key={d} className="text-center font-semibold uppercase text-slate-400">{d}</span>)}
      {PREVIEW.map(([name, cells]) => (
        <div key={name} className="contents">
          <span className="self-center font-semibold text-slate-700">{name}</span>
          {cells.map((c, i) => c ? (
            <div key={i} className="rounded-md border border-slate-200 bg-slate-50 px-1 py-1.5" style={{ borderLeft: `3px solid ${c[2]}` }}>
              <p className="font-mono font-semibold">{c[0]}</p>
              <p className="font-mono text-slate-500">{c[1]}{c[3] && <span className="ml-0.5 rounded bg-slate-900 px-0.5 text-white">+1</span>}</p>
            </div>
          ) : <div key={i} className="rounded-md border border-dashed border-slate-200" />)}
        </div>
      ))}
    </div>
  </div>
);

const Pricing = ({ plans, cms }) => (
  <section id="pricing" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12">
    <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">{cms.pricing_eyebrow || "Pricing in GBP"}</p>
    <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl" data-testid="pricing-title">{cms.pricing_title || "Simple monthly plans. Cancel any time."}</h2>
    <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      {plans.map((p) => (
        <div key={p.id} className={`lift flex flex-col rounded-2xl border p-8 ${p.highlighted ? "border-indigo-600 bg-slate-950 text-white" : "border-slate-200 bg-white"}`} data-testid={`pricing-card-${p.code}`}>
          <p className="font-display text-xl font-bold">{p.name}</p>
          <p className={`mt-1 text-sm ${p.highlighted ? "text-slate-300" : "text-slate-500"}`}>{p.description}</p>
          <p className="mt-6 font-display text-4xl font-extrabold">{p.price > 0 ? money(p.price).replace(".00", "") : "£0"}<span className="text-sm font-medium opacity-60">/mo</span></p>
          <ul className="mt-6 flex-1 space-y-2 text-sm">
            {p.features.map((f) => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{f}</li>)}
          </ul>
          <Link to={`/signup?plan=${p.code}`} className={`mt-8 rounded-full px-4 py-2.5 text-center text-sm font-semibold transition-colors ${p.highlighted ? "bg-indigo-500 hover:bg-indigo-400" : "bg-slate-900 text-white hover:bg-slate-700"}`} data-testid={`pricing-cta-${p.code}`}>
            {p.price > 0 ? "Choose plan" : "Start free"}
          </Link>
        </div>
      ))}
    </div>
  </section>
);

export default function Landing() {
  usePageMeta("home");
  const [cms, setCms] = useState(null);
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get("/public/cms").then((r) => setCms(r.data));
    api.get("/public/plans").then((r) => setPlans(r.data));
  }, []);
  if (!cms) return <div className="min-h-screen" />;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="grid-bg relative">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-8 lg:grid-cols-[1.1fr_1fr] lg:px-12 lg:py-28">
          <div>
            <p className="rise inline-block rounded-full border border-indigo-200 bg-white px-3 py-1 text-xs font-semibold text-indigo-700" data-testid="hero-eyebrow">{cms.hero_eyebrow}</p>
            <h1 className="rise d1 mt-6 font-display text-4xl font-extrabold tracking-tight text-slate-950 sm:text-5xl lg:text-6xl" data-testid="hero-title">{cms.hero_title}</h1>
            <p className="rise d2 mt-6 max-w-xl text-base text-slate-600 sm:text-lg">{cms.hero_subtitle}</p>
            <div className="rise d3 mt-10 flex flex-wrap gap-3">
              <Link to="/signup" className="flex items-center gap-2 rounded-full bg-indigo-600 px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 hover:bg-indigo-700" data-testid="hero-cta-btn">{cms.hero_cta} <ArrowRight className="h-4 w-4" /></Link>
              <DemoButton className="rounded-full border border-indigo-200 bg-indigo-50 px-6 py-3 text-sm font-semibold text-indigo-700 hover:bg-indigo-100" testId="hero-demo-btn" />
              <Link to="/tour" className="rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50" data-testid="hero-tour-btn">Take the tour</Link>
            </div>
            <div className="rise d4 mt-12 flex flex-wrap gap-10">
              {(cms.stats || []).map((s) => (
                <div key={s.label}><p className="font-display text-3xl font-bold text-slate-950">{s.value}</p><p className="text-xs text-slate-500">{s.label}</p></div>
              ))}
            </div>
          </div>
          <RotaPreview />
        </div>
      </section>

      <section id="features" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12">
        <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-4xl" data-testid="features-title">{cms.features_title || "Everything between the rota and the payslip."}</h2>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-6">
          {(cms.features || []).map((f, i) => {
            const Icon = ICONS[i % ICONS.length];
            const wide = i === 0 || i === 1;
            return (
              <div key={f.title} className={`lift rounded-2xl border border-slate-200 p-8 ${wide ? "md:col-span-3" : "md:col-span-2"} ${i === 1 ? "bg-purple-50" : "bg-slate-50"}`} data-testid={`feature-card-${i}`}>
                <Icon className={`h-6 w-6 ${i === 1 ? "text-purple-600" : "text-indigo-600"}`} />
                <p className="mt-4 font-display text-xl font-semibold">{f.title}</p>
                <p className="mt-2 text-sm text-slate-600">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-slate-950 text-white">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-24 sm:px-8 lg:grid-cols-2 lg:px-12">
          <img src={cms.care_image || "/images/care.jpg"} alt={cms.care_title || "Care worker using a tablet"} className="h-80 w-full rounded-2xl object-cover" data-testid="care-image" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-purple-300">{cms.care_eyebrow || "Built for care & 24/7 teams"}</p>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl" data-testid="care-title">{cms.care_title || "10:00 Monday to 10:30 Tuesday? No problem."}</h2>
            <p className="mt-4 text-slate-300">{cms.care_body || "Set up sleepover shift types with a flat £ allowance and unpaid sleeping hours. Overnight shifts show clearly on the rota and timesheets calculate pay automatically."}</p>
          </div>
        </div>
      </section>

      <Pricing plans={plans} cms={cms} />

      <section id="faq" className="mx-auto max-w-3xl px-4 pb-24 sm:px-8">
        <h2 className="font-display text-3xl font-bold tracking-tight" data-testid="faq-title">{cms.faq_title || "Questions"}</h2>
        <Accordion type="single" collapsible className="mt-6">
          {(cms.faq || []).map((f, i) => (
            <AccordionItem key={i} value={`q${i}`}>
              <AccordionTrigger data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent>{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <footer className="border-t border-slate-200 py-10">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-8 lg:px-12">
          <Logo />
          <p data-testid="footer-text">{cms.footer_text}</p>
        </div>
      </footer>
    </div>
  );
}
